/**
 * Returns the adjusted y-position for a context-value indicator.
 * Prevents label overlap when min/max values are too close together
 * and clamps the result so the text label stays within the chart area.
 *
 * @param rawMinYPos
 * @param rawMaxYPos
 * @param otherIndicatorVisible - Whether the other indicator is rendered.
 * @param isMax - True when computing the position for the max indicator.
 * @param minLabelsDistance - Minimum vertical gap (px) between the two indicators
 *   to prevent overlap. Use `CONTEXT_VALUE_MIN_LABELS_DISTANCE_SMALL` (24 px)
 *   or `CONTEXT_VALUE_MIN_LABELS_DISTANCE_LARGE` (32 px) depending on font size.
 * @param textOffset - Space (px) the label needs above its anchor. Used to
 *   clamp the max label within the top boundary.
 * @param chartHeight - Chart area height (px). When set, the min label is
 *   clamped so it doesn't overflow below the chart.
 * @param textBottomOverflow - How far (px) the label extends below its anchor
 *   (`lineHeight − textOffset`, typically 4 px). When omitted, no bottom clamping is applied.
 */
export function getAdjustedContextValueYPosition(
  rawMinYPos: number,
  rawMaxYPos: number,
  otherIndicatorVisible: boolean,
  isMax: boolean,
  minLabelsDistance: number,
  textOffset: number,
  chartHeight?: number,
  textBottomOverflow?: number,
): number {
  const currentVerticalGap = rawMinYPos - rawMaxYPos;
  const needsOverlapAdjustment =
    otherIndicatorVisible && currentVerticalGap < minLabelsDistance;

  let position: number;

  if (needsOverlapAdjustment) {
    const requiredShift = (minLabelsDistance - currentVerticalGap) / 2;
    position = isMax ? rawMaxYPos - requiredShift : rawMinYPos + requiredShift;
  } else {
    position = isMax ? rawMaxYPos : rawMinYPos;
  }

  // Clamp so the text label stays within the chart boundaries.
  if (isMax) {
    // Max label: text is rendered at (position − textOffset), must be ≥ 0.
    position = Math.max(position, textOffset);
  } else if (chartHeight !== undefined && textBottomOverflow !== undefined) {
    // Min label: text bottom is at (position + textBottomOverflow), must be ≤ chartHeight.
    position = Math.min(position, chartHeight - textBottomOverflow);
  }

  return position;
}

interface ContextValueYPositionsParams {
  rawMinY: number;
  rawMaxY: number;
  showContextMin: boolean;
  showContextMax: boolean;
  minLabelsDistance: number;
  minTextOffset: number;
  maxTextOffset: number;
  chartHeight: number;
}

/**
 * Computes the adjusted Y positions for both context value labels (min & max)
 * in a single call by delegating to `getAdjustedContextValueYPosition`.
 */
export function getContextValueYPositions({
  rawMinY,
  rawMaxY,
  showContextMin,
  showContextMax,
  minLabelsDistance,
  minTextOffset,
  maxTextOffset,
  chartHeight,
}: ContextValueYPositionsParams): {
  adjustedMinLabelY: number;
  adjustedMaxLabelY: number;
} {
  return {
    adjustedMinLabelY: getAdjustedContextValueYPosition(
      rawMinY,
      rawMaxY,
      showContextMax,
      false,
      minLabelsDistance,
      minTextOffset,
      chartHeight,
      maxTextOffset,
    ),
    adjustedMaxLabelY: getAdjustedContextValueYPosition(
      rawMinY,
      rawMaxY,
      showContextMin,
      true,
      minLabelsDistance,
      maxTextOffset,
    ),
  };
}
